import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TimePickerCustom from 'src/components/common/field/TimePickerCustom';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import { postAPI, postFormDataAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import ConfirmDelete from 'src/components/common/modal/ConfirmDelete';

const rowStyle = {
  '&:last-child td, &:last-child th': { border: 0 },
  '&.MuiTableRow-root .MuiTableCell-root.MuiTableCell-body': {
    py: '16px',
    borderBottom: '1px solid #F3F3F3',
  },
};

const defaultTime = {
  start: `${dayjs().get('hour')}:${dayjs().get('minute')}`,
  end: `${dayjs().get('hour')}:${dayjs().get('minute')}`,
};

type DataType = {
  categoryid: number | null;
  doctorid: number | null;
  id: number | null;
  mintime: string | null;
  status: 1;
  times: any[] | null;
  typeid: number | null;
  visittime: string | null;
};

const Edit = (props: any) => {
  const navigate = useNavigate();
  const categories = props.data?.categories || null;
  const doctors = !props.timeid
    ? props.data?.doctors
    : props.data?.doctors.filter(
        (doc: any) => String(doc.id) === String(props.doctorid)
      );
  const mintimes = props.data?.mintimes || null;
  const time = props.data?.time || null;
  const types = props.data?.types || null;
  // const dataDoctors = props.dataDoctors || [];
  const [openConfirm, setOpenConfirm] = useState(false);
  const [deleteTimeId, setDeleteTimeId] = useState(0);

  const notifyContext = useNotify();
  //   const [errors, setErrors] = useState(defaultError);
  const [data, setData] = useState<DataType>({
    categoryid: null,
    doctorid: null,
    id: null,
    mintime: null,
    status: 1,
    times: null,
    typeid: null,
    visittime: null,
  });

  useEffect(() => {
    setData({
      categoryid: time ? (time.categoryid ? time.categoryid : null) : null,
      doctorid: time && time.doctorid ? time.doctorid : props.doctorid || null,
      id: time ? (time.id ? time.id : null) : null,
      mintime: time ? (time.mintime ? time.mintime : null) : null,
      status: time ? (time.status ? time.status : 1) : 1,
      times: !props.timeid
        ? [defaultTime]
        : time
        ? time.times
          ? time.times
          : null
        : null,
      typeid: time ? (time.typeid ? time.typeid : null) : null,
      visittime: time ? (time.visittime ? time.visittime : null) : null,
    });
  }, [time, props.doctorid, props.timeid]);

  const handleSelectChange = (event: SelectChangeEvent<number | string>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleChange =
    (name: string, index: number) =>
    (id: number | string | number[] | null) => {
      const dateTimes = data.times || [];
      const modifiedData = dateTimes.map((item: any, idx: number) => {
        if (idx === index) {
          return { ...item, [name]: id };
        }
        return item;
      });
      setData((prevState) => ({
        ...prevState,
        times: modifiedData,
      }));
    };

  const handleChangeTime =
    (name: string, index: number) => (date: Dayjs | null) => {
      const timeFormat = date ? date.format('HH:mm') : null;
      handleChange(name, index)(timeFormat);
    };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setData((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    navigate(`/master/time?doctorid=${props.doctorid}`);
  };

  const handleCloseConfirmDelete = () => {
    setOpenConfirm(false);
  };
  const handleDeleteClick = (id: number) => {
    setOpenConfirm(true);
    setDeleteTimeId(id);
  };

  const handleDelete = () => {
    setOpenConfirm(false);
    const token = getToken();
    const formData = new FormData();
    formData.append('id', deleteTimeId.toString());

    try {
      postFormDataAPI(`/master/time-delete?token=${token}`, formData).then(
        (res) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '削除しました。',
              type: 'success',
            });
            navigate(`/master/time?doctorid=${props.doctorid}`);
          }
          if (res.code === 500 || res.code === 501) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error'
            });
          }
        }
      );
    } catch (error) {
      console.error('Failed to delete data:', error);
      return;
    }
  };

  const handleSave = () => {
    const dataForm = {
      ...data,
      status: data.status === 1 ? true : false,
      times: JSON.stringify(data.times),
    };

    // if (!validateForm()) {
    //   return;
    // }
    try {
      const token = getToken();
      postAPI(`/master/time-save?token=${token}`, { ...dataForm }).then(
        (res) => {
          if (res && res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            navigate(`/master/time?doctorid=${props.doctorid}`);
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        }
      );
    } catch (error) {
      console.error('Failed to post data:', error);
      return;
    }
  };

  const handleAddTime = () => {
    const modifiedData = data.times
      ? [...data.times, defaultTime]
      : [defaultTime];

    setData((prevState) => ({
      ...prevState,
      times: modifiedData,
    }));
  };

  const handleDeleteTimeClick = (index: number) => {
    let modifiedData = data.times ? [...data.times] : [];
    modifiedData.splice(index, 1);
    setData((prevState) => ({
      ...prevState,
      times: modifiedData,
    }));
  };

  return (
    <Stack>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          width: '100%',
          height: '100%',
        }}
      >
        <Table
          sx={{
            minWidth: 470,
            '& .MuiTableCell-root.MuiTableCell-head': {
              borderBottom: '1px solid #F3F3F3',
              fontWeight: 700,
              p: '6px 16px',
              fontSize: '14px',
            },
            '& .MuiTableCell-root.MuiTableCell-body': {
              color: 'primary.dark',
              fontSize: '14px',
              py: '6px',
            },
          }}
          aria-label='simple table'
        >
          <TableBody sx={{ '&.MuiTableBody-root th': { fontWeight: 700 } }}>
            <TableRow sx={rowStyle}>
              <TableCell component={'th'}>医師</TableCell>
              <TableCell>
                {doctors && time && (
                  <Select
                    className='select-status'
                    value={data.doctorid || ''}
                    name='doctorid'
                    onChange={(event) => handleSelectChange(event)}
                    sx={{
                      '& .MuiSelect-select.MuiSelect-outlined': {
                        py: '5px',
                      },
                      width: '100%',
                    }}
                  >
                    {doctors.map((doctor: any, index: number) => {
                      if (props.timeid && doctor.id !== data.doctorid)
                        return (
                          <MenuItem key={index} value={doctor.id}>
                            a
                          </MenuItem>
                        );
                      return (
                        <MenuItem key={index} value={doctor.id}>
                          {doctor.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </TableCell>
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell component={'th'}>カテゴリー</TableCell>
              <TableCell>
                {categories && (
                  <Select
                    className='select-status'
                    value={data?.categoryid || ''}
                    name='categoryid'
                    onChange={(event) => handleSelectChange(event)}
                    sx={{
                      '& .MuiSelect-select.MuiSelect-outlined': {
                        py: '5px',
                      },
                      width: '100%',
                    }}
                  >
                    {categories.map((category: any, index: number) => {
                      return (
                        <MenuItem key={index} value={category.id}>
                          {category.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </TableCell>
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell component={'th'}>予約方法</TableCell>
              <TableCell>
                {types && (
                  <RadioGroup
                    row
                    aria-labelledby='demo-controlled-radio-buttons-group'
                    name='typeid'
                    value={data?.typeid || 0}
                    onChange={handleRadioChange}
                  >
                    {types.map((type: any, idx: number) => {
                      return (
                        <FormControlLabel
                          key={idx}
                          value={type.value}
                          control={<Radio />}
                          label={type.label}
                        />
                      );
                    })}
                  </RadioGroup>
                )}
              </TableCell>
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell component={'th'}>予約可能時間</TableCell>
              <TableCell>
                <Stack spacing={'8px'}>
                  {data?.times &&
                    Array.isArray(data?.times) &&
                    (data?.times as any).map((time: any, index: number) => {
                      return (
                        <Stack
                          key={index}
                          direction={'row'}
                          justifyContent='left'
                          spacing={'8px'}
                          sx={{
                            '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root':
                              {
                                width: '116px',
                              },
                          }}
                        >
                          <Stack
                            sx={{
                              '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: !true
                                    ? '#d32f2f !important'
                                    : '',
                                },
                            }}
                          >
                            <TimePickerCustom
                              value={dayjs(
                                dayjs().format('YYYY-MM-DD') + ' ' + time.start
                              )}
                              onChange={handleChangeTime('start', index)}
                            />
                          </Stack>
                          <Stack
                            sx={{
                              '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: !true
                                    ? '#d32f2f !important'
                                    : '',
                                },
                            }}
                          >
                            <TimePickerCustom
                              value={dayjs(
                                dayjs().format('YYYY-MM-DD') + ' ' + time.end
                              )}
                              onChange={handleChangeTime('end', index)}
                            />
                          </Stack>
                          <Box
                            component={'img'}
                            src='/images/icons/ic-trash.svg'
                            alt='trash'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleDeleteTimeClick(index)}
                          />
                        </Stack>
                      );
                    })}
                  <BtnOutlineGradient
                    text='+'
                    handleButtonClick={handleAddTime}
                  />
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell component={'th'}>時間枠</TableCell>
              <TableCell>
                {mintimes && data?.categoryid !== 6 && (
                  <RadioGroup
                    row
                    aria-labelledby='demo-controlled-radio-buttons-group'
                    name='mintime'
                    value={data?.mintime}
                    onChange={handleRadioChange}
                  >
                    {mintimes.map((min: any, idx: number) => {
                      return (
                        <FormControlLabel
                          key={idx}
                          value={min}
                          control={<Radio />}
                          label={`${min}分`}
                        />
                      );
                    })}
                  </RadioGroup>
                )}
                {data?.categoryid === 6 && <>*見積もりに記載</>}
              </TableCell>
            </TableRow>
            <TableRow sx={rowStyle}>
              <TableCell component={'th'}>来院枠</TableCell>
              <TableCell>
                <OutlinedInput
                  sx={{
                    fontSize: '14px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#BDBDBD',
                    },
                    '& .MuiInputBase-input': { padding: '8px 14px' },
                    '& input::placeholder': {
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#CCD1D9',
                      opacity: 1,
                    },
                    backgroundColor: 'primary.light',
                    width: '100%',
                  }}
                  id='visittime'
                  endAdornment={
                    <InputAdornment position='end'>分</InputAdornment>
                  }
                  aria-describedby='outlined-weight-helper-text'
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  value={data?.visittime || ''}
                  type='number'
                  name='visittime'
                  onChange={(event) => handleInputChange(event)}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction={'row'} spacing={'20px'} mt={'32px'}>
        <BtnOutlineGradient
          text='キャンセル'
          handleButtonClick={handleCancel}
        />
        {props.timeid && (
          <BtnOutlineGradient
            text='削除'
            handleButtonClick={() => handleDeleteClick(props.timeid)}
            grandient='#DA4453'
            color='#DA4453'
          />
        )}
        <BtnGradient text='登録' handleButtonClick={handleSave} />
      </Stack>
      <ConfirmDelete
        openConfirm={openConfirm}
        handleCloseConfirmDelete={handleCloseConfirmDelete}
        handleDelete={handleDelete}
      />
    </Stack>
  );
};

export default Edit;
